import {
  CANONICAL_SUBSEGMENTS,
  CANONICAL_SUBSEGMENT_TOKEN,
  SPECIALTIES,
  SPECIALTY_PARENT,
  type CanonicalSubsegment,
  type SpecialtyToken,
} from './taxonomy.js';

// Market / Opportunity panel — the sub-segment axis of the `bucket × region`
// consolidation matrix.
//
// ── 2026-07-20 REWRITE ────────────────────────────────────────────────────────
// This axis used to be a fixed 10 buckets crosswalked from the LEGACY 23-value
// `entities.sub_segment` enum. Two things were wrong with that:
//
//   1. `entities.sub_segment` is populated in SIX markets only (GB, BE, FR, NO,
//      DK, EE — measured on prod: the other 21 hold exactly zero). That single
//      column is why the materialised matrix only ever produced cells for those
//      six. The canonical `entity_normalised_metrics.subsegment` is populated
//      for 532,652 of 532,655 in-scope rows across ALL 27 markets, so moving the
//      axis onto it is both a correctness fix and a 21-market unlock, with no
//      new data required.
//   2. The bucket list folded the universe into 10 rows and dropped seven
//      sub-segments entirely as "not fragmented-clinic roll-up territory" — a
//      UK-thesis judgement made before the 27-market universe existed. It threw
//      away Pharmacy (89,663 operators), Primary care (57,949), Hospitals &
//      clinics, Pharma wholesale and Medical equipment, all of which are live
//      European consolidation plays.
//
// The axis is now **finest-else-parent** over the platform's real taxonomy
// (Alex, 2026-07-20): every one of the 18 canonical sub-segments gets a row, and
// the 10 fine specialties beneath the 3 expanded parents get their own rows
// where they resolve. An operator lands on its specialty row if one resolved,
// otherwise on its parent row — so nothing is ever lost and parent + children
// always sum to the whole sub-segment.
//
// ⚠️ READ THIS BEFORE INTERPRETING A PARENT ROW. For the 3 expanded parents the
// parent row is the RESIDUAL, not the total: `primary_specialty` resolves for
// only ~17% of the universe and is literally zero in 8 markets (AT, CH, GR, HR,
// HU, PT, RO, SI), so a fine row reads as "at least this many" and the parent
// carries everything unresolved. Their labels say "— other" for exactly that
// reason. The other 15 parents have no children and their rows are complete.

/** The matrix rows: 18 canonical parents + the 10 fine specialties, in the same
 *  hierarchical order the Screener's sub-segment filter uses (each parent
 *  immediately followed by its children), so the two surfaces read alike.
 *
 *  Keys are the stable machine tokens from taxonomy.ts — canonical tokens for
 *  parents, specialty tokens for children. Kept as an explicit literal (rather
 *  than generated) so the list is greppable and reviewable; `market-opportunity.test.ts`
 *  asserts it stays in lockstep with CANONICAL_SUBSEGMENTS + SPECIALTIES. */
export const OPPORTUNITY_BUCKETS = [
  { key: 'hospitals_clinics', label: 'Hospitals & clinics', parent: null },
  { key: 'primary_care_gp', label: 'Primary care (GP)', parent: null },
  { key: 'specialist_medical', label: 'Specialist medical — other', parent: null },
  { key: 'dialysis', label: 'Dialysis / renal care', parent: 'specialist_medical' },
  { key: 'oncology', label: 'Oncology / cancer care', parent: 'specialist_medical' },
  { key: 'radiotherapy', label: 'Radiotherapy', parent: 'specialist_medical' },
  { key: 'fertility_ivf', label: 'Fertility / IVF', parent: 'specialist_medical' },
  { key: 'ophthalmology', label: 'Ophthalmology', parent: 'specialist_medical' },
  { key: 'dental', label: 'Dental', parent: null },
  { key: 'optical', label: 'Optical', parent: null },
  { key: 'audiology', label: 'Audiology', parent: null },
  { key: 'physio_allied', label: 'Physiotherapy & allied health', parent: null },
  { key: 'diagnostics_labs', label: 'Diagnostics & labs — other', parent: null },
  { key: 'imaging', label: 'Imaging', parent: 'diagnostics_labs' },
  { key: 'pathology', label: 'Pathology / labs', parent: 'diagnostics_labs' },
  { key: 'pharmacy', label: 'Pharmacy', parent: null },
  { key: 'pharma_wholesale', label: 'Pharma wholesale', parent: null },
  { key: 'medical_equipment', label: 'Medical equipment & supplies', parent: null },
  { key: 'dental_lab_equipment', label: 'Dental laboratories & equipment', parent: null },
  { key: 'veterinary', label: 'Veterinary', parent: null },
  { key: 'residential_elderly', label: 'Residential elderly care — other', parent: null },
  { key: 'nursing_home', label: 'Nursing home (with nursing)', parent: 'residential_elderly' },
  {
    key: 'residential_care',
    label: 'Residential care (no nursing)',
    parent: 'residential_elderly',
  },
  { key: 'assisted_living', label: 'Assisted living', parent: 'residential_elderly' },
  { key: 'residential_specialist', label: 'Residential specialist care', parent: null },
  { key: 'domiciliary_home', label: 'Domiciliary / home care', parent: null },
  { key: 'ambulance_transport', label: 'Ambulance / patient transport', parent: null },
  { key: 'other_healthcare', label: 'Other healthcare', parent: null },
] as const;

export type OpportunityBucket = (typeof OPPORTUNITY_BUCKETS)[number]['key'];

export const OPPORTUNITY_BUCKET_LABELS: Record<OpportunityBucket, string> = Object.fromEntries(
  OPPORTUNITY_BUCKETS.map((b) => [b.key, b.label]),
) as Record<OpportunityBucket, string>;

/** Bucket → its parent bucket, or null for a top-level row. Drives the panel's
 *  indentation, so a reader can see the children sit beneath the residual. */
export const OPPORTUNITY_BUCKET_PARENT: Record<OpportunityBucket, OpportunityBucket | null> =
  Object.fromEntries(OPPORTUNITY_BUCKETS.map((b) => [b.key, b.parent])) as Record<
    OpportunityBucket,
    OpportunityBucket | null
  >;

/** Canonical sub-segment (the `enm.subsegment` display label) → its parent
 *  bucket. Total by construction: every canonical value has a row, nothing is
 *  dropped, so no operator can fall out of the matrix. */
export const CANONICAL_TO_OPPORTUNITY_BUCKET: Record<CanonicalSubsegment, OpportunityBucket> =
  Object.fromEntries(
    CANONICAL_SUBSEGMENTS.map((c) => [c, CANONICAL_SUBSEGMENT_TOKEN[c]]),
  ) as Record<CanonicalSubsegment, OpportunityBucket>;

/** Fine specialty token paired with the canonical parent it is only valid
 *  beneath. **The pairing is a guard, not decoration:** `enm.primary_specialty`
 *  carries vocabulary drift — stray `dental` / `optical` / `veterinary` values
 *  and the three `*_other` residual-model buckets all live in that column
 *  alongside the 10 sanctioned tokens. A specialty is honoured ONLY when the
 *  row's canonical sub-segment is its declared parent; everything else falls
 *  through to the parent row rather than inventing one. */
export const SPECIALTY_BUCKET_PAIRS: ReadonlyArray<{
  specialty: SpecialtyToken;
  canonicalParent: CanonicalSubsegment;
}> = SPECIALTIES.map((s) => ({ specialty: s.token, canonicalParent: SPECIALTY_PARENT[s.token] }));

/** The Screener filter a bucket drills into.
 *
 *  ⚠️ NOT the legacy `subSegmentCanonical` param — that filters
 *  `entities.sub_segment`, which is empty in 21 markets, so the old drill
 *  silently returned an empty Screener everywhere outside the six. Parent
 *  buckets filter the canonical `subsegment`; specialty buckets filter
 *  `specialty`. The Screener ORs those two axes, which is exactly the
 *  finest-else-parent read.
 *
 *  A parent drill deliberately returns the WHOLE sub-segment (residual +
 *  resolved children), not just the residual: a user clicking "Specialist
 *  medical" wants the segment, and the cell they clicked is the residual only
 *  because of detection coverage — which is a fact about our data, not about
 *  the market. */
export function screenerFilterForBucket(bucket: OpportunityBucket): {
  subsegment?: string[];
  specialty?: string[];
} {
  const pair = SPECIALTY_BUCKET_PAIRS.find((p) => p.specialty === (bucket as string));
  if (pair) return { specialty: [pair.specialty] };
  const canonical = CANONICAL_SUBSEGMENTS.find(
    (c) => CANONICAL_SUBSEGMENT_TOKEN[c] === (bucket as string),
  );
  return canonical ? { subsegment: [canonical] } : {};
}

/** The `ownership_type` value that defines an "independent" operator for the
 *  Independent supply metric and the Market Opportunity drill.
 *
 *  INDEPENDENCE ≠ INDIVIDUAL CONTROL (refined model). The acquirable universe is
 *  every operator that STANDS ALONE — not sponsor-backed (pe_platform/pe_add_on)
 *  and not inside a corporate/strategic group — REGARDLESS of internal share-
 *  holding. A sole founder, a 3–4 partner practice, a multi-family holding and a
 *  dispersed cap table are all equally independent and equally acquirable.
 *  Independence is resolved from the economic-group engine: `economic_group IS
 *  NULL` (not in a resolved concern / no sponsor in the chain) AND ownership_type
 *  is not corporate-owned. "No single 75%+ controller" (partner-owned) therefore
 *  QUALIFIES — dropping it (the old rule) would lose every partner practice, the
 *  prime bolt-ons. Where the chain can't be resolved but nothing points to a
 *  group, the row is still independent, flagged `independence: proxy`.
 *
 *  The ≥75% facts are SUB-FLAGS (sorts, not the gate):
 *    control = individual_75plus | multi_owner | dispersed
 *    family_founder = (control == individual_75plus)  → succession-relevant sort.
 *
 *  INDEPENDENT_OWNERSHIP_TYPE is kept only for the family_founder sub-flag, NOT
 *  as the target gate; CORPORATE_OWNERSHIP_TYPE is the exclusion (in a group). */
export const INDEPENDENT_OWNERSHIP_TYPE = 'Individual-controlled (75%+)';
export const CORPORATE_OWNERSHIP_TYPE = 'Corporate-owned (75%+)';
